import { AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { XeroBulkSyncStatusEntry } from "@workspace/api-client-react";
import logoXero from "@/assets/logo-xero.png";

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

type Props = {
  status: XeroBulkSyncStatusEntry | undefined;
  kind: "invoice" | "estimate";
};

export function XeroSyncBadge({ status, kind }: Props) {
  if (!status) return null;
  const hasPushed = !!status.externalId || !!status.lastSuccessAt;
  const failed = !!status.lastFailedAt;
  if (!hasPushed && !failed) return null;

  const docLabel = kind === "invoice" ? "Invoice" : "Quote";
  const lastPushedAbsolute = status.lastSuccessAt
    ? new Date(status.lastSuccessAt).toLocaleString()
    : null;

  let tooltip: string;
  if (failed) {
    tooltip = lastPushedAbsolute
      ? `Last push to Xero failed · last successful push ${lastPushedAbsolute}`
      : `Last push to Xero failed`;
  } else if (lastPushedAbsolute) {
    tooltip = `Last pushed ${lastPushedAbsolute}`;
  } else {
    tooltip = `${docLabel} is in Xero`;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span
          className="inline-flex items-center shrink-0"
          data-testid={`badge-xero-${kind}`}
          aria-label={tooltip}
          title={
            failed
              ? "Last push to Xero failed"
              : status.lastSuccessAt
                ? `Last pushed ${formatRelative(status.lastSuccessAt)}`
                : `${docLabel} is in Xero`
          }
        >
          {failed ? (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-amber-100 text-amber-700 border border-amber-300">
              <AlertCircle className="w-2.5 h-2.5" />
            </span>
          ) : (
            <img src={logoXero} alt="Xero" className="w-4 h-4 object-contain rounded-sm" />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
